import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  employees,
  initialFollowUps,
  initialInquiries,
} from '../data/mockData'

const InquiryContext = createContext(null)

/** Simulated network delay for skeleton UX */
const delay = (ms) => new Promise((r) => setTimeout(r, ms))

function generateId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function InquiryProvider({ children }) {
  const [inquiries, setInquiries] = useState(initialInquiries)
  const [followUps, setFollowUps] = useState(initialFollowUps)
  const [loading, setLoading] = useState(false)
  const [bootstrapping, setBootstrapping] = useState(true)

  useEffect(() => {
    let cancelled = false
    delay(500).then(() => {
      if (!cancelled) setBootstrapping(false)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const withLoading = useCallback(async (fn) => {
    setLoading(true)
    try {
      await delay(450)
      return await fn()
    } finally {
      setLoading(false)
    }
  }, [])

  const getEmployeeName = useCallback((empId) => {
    if (!empId) return '—'
    return employees.find((e) => e.id === empId)?.name ?? '—'
  }, [])

  const getInquiryById = useCallback(
    (id) => inquiries.find((i) => i.id === id),
    [inquiries],
  )

  const getFollowUpsForInquiry = useCallback(
    (inquiryId) =>
      followUps
        .filter((f) => f.inquiryId === inquiryId)
        .sort((a, b) => new Date(b.date) - new Date(a.date)),
    [followUps],
  )

  const addInquiry = useCallback(
    (payload) =>
      withLoading(() => {
        const id = generateId('inq')
        const row = {
          id,
          name: payload.name.trim(),
          phone: payload.phone.trim(),
          email: payload.email.trim(),
          service: payload.service,
          source: payload.source,
          priority: payload.priority,
          status: payload.status || 'New',
          assignedTo: payload.assignedTo || null,
          nextFollowUpDate: payload.nextFollowUpDate || null,
          remarks: (payload.remarks || '').trim(),
          createdAt: new Date().toISOString(),
        }
        setInquiries((prev) => [row, ...prev])
        return id
      }),
    [withLoading],
  )

  const updateInquiry = useCallback(
    (id, payload) =>
      withLoading(() => {
        setInquiries((prev) =>
          prev.map((i) =>
            i.id === id
              ? {
                  ...i,
                  ...payload,
                  name: payload.name != null ? String(payload.name).trim() : i.name,
                  phone:
                    payload.phone != null ? String(payload.phone).trim() : i.phone,
                  email:
                    payload.email != null ? String(payload.email).trim() : i.email,
                  remarks:
                    payload.remarks != null
                      ? String(payload.remarks).trim()
                      : i.remarks,
                }
              : i,
          ),
        )
      }),
    [withLoading],
  )

  const deleteInquiry = useCallback(
    (id) =>
      withLoading(() => {
        setInquiries((prev) => prev.filter((i) => i.id !== id))
        setFollowUps((prev) => prev.filter((f) => f.inquiryId !== id))
      }),
    [withLoading],
  )

  const updateInquiryStatus = useCallback(
    (id, status) =>
      withLoading(() => {
        setInquiries((prev) =>
          prev.map((i) => (i.id === id ? { ...i, status } : i)),
        )
      }),
    [withLoading],
  )

  const convertToCustomer = useCallback(
    (id) =>
      withLoading(() => {
        setInquiries((prev) =>
          prev.map((i) =>
            i.id === id ? { ...i, status: 'Converted', nextFollowUpDate: null } : i,
          ),
        )
      }),
    [withLoading],
  )

  /** Instant status change for Kanban drag-and-drop (no artificial delay). */
  const moveInquiryToStatus = useCallback((id, status) => {
    setInquiries((prev) =>
      prev.map((i) => (i.id === id ? { ...i, status } : i)),
    )
  }, [])

  const addFollowUp = useCallback(
    (inquiryId, { type, notes, outcome, nextFollowUpDate }) =>
      withLoading(() => {
        const fu = {
          id: generateId('fu'),
          inquiryId,
          date: new Date().toISOString(),
          type,
          notes: (notes || '').trim(),
          outcome,
          nextFollowUpDate: nextFollowUpDate || null,
        }
        setFollowUps((prev) => [fu, ...prev])
        setInquiries((prev) =>
          prev.map((i) =>
            i.id === inquiryId
              ? { ...i, nextFollowUpDate: nextFollowUpDate || i.nextFollowUpDate }
              : i,
          ),
        )
      }),
    [withLoading],
  )

  const busy = bootstrapping || loading

  const value = useMemo(
    () => ({
      employees,
      inquiries,
      followUps,
      loading,
      bootstrapping,
      busy,
      getEmployeeName,
      getInquiryById,
      getFollowUpsForInquiry,
      addInquiry,
      updateInquiry,
      deleteInquiry,
      updateInquiryStatus,
      convertToCustomer,
      addFollowUp,
      moveInquiryToStatus,
    }),
    [
      inquiries,
      followUps,
      loading,
      bootstrapping,
      busy,
      getEmployeeName,
      getInquiryById,
      getFollowUpsForInquiry,
      addInquiry,
      updateInquiry,
      deleteInquiry,
      updateInquiryStatus,
      convertToCustomer,
      addFollowUp,
      moveInquiryToStatus,
    ],
  )

  return (
    <InquiryContext.Provider value={value}>{children}</InquiryContext.Provider>
  )
}

export function useInquiry() {
  const ctx = useContext(InquiryContext)
  if (!ctx) {
    throw new Error('useInquiry must be used within InquiryProvider')
  }
  return ctx
}
